import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';

const SubscriptionContext = createContext();

// Subscription tiers configuration
export const SUBSCRIPTION_TIERS = {
  FREE: {
    id: 'free',
    name: { fr: 'Gratuit', en: 'Free' },
    price: 0,
    analysesPerMonth: 1,
    features: {
      unlimitedAnalyses: false,
      financialEstimates: false, // Locked
      pdfReports: false, // Locked
      planLibrary: false, // Locked
      projectImplementation: false // Locked
    }
  },
  PRO: {
    id: 'pro',
    name: { fr: 'Pro', en: 'Pro' },
    price: 49,
    analysesPerMonth: Infinity,
    features: {
      unlimitedAnalyses: true,
      financialEstimates: true, // Unlocked
      pdfReports: false, // Locked
      planLibrary: false, // Locked
      projectImplementation: false // Locked
    }
  },
  DEVELOPER: {
    id: 'developer',
    name: { fr: 'Développeur', en: 'Developer' },
    price: 149,
    analysesPerMonth: Infinity,
    features: {
      unlimitedAnalyses: true,
      financialEstimates: true, // Unlocked
      pdfReports: true, // Unlocked
      planLibrary: true, // Unlocked
      projectImplementation: true // Unlocked
    }
  }
};

export const SubscriptionProvider = ({ children }) => {
  const { user } = useAuth();
  
  // Get subscription from localStorage or default to FREE
  const [subscription, setSubscription] = useState(() => {
    const saved = localStorage.getItem('plexterra_subscription');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        return { tier: 'FREE', analysesUsed: 0, lastResetDate: new Date().toISOString().slice(0, 7) };
      }
    }
    return { tier: 'FREE', analysesUsed: 0, lastResetDate: new Date().toISOString().slice(0, 7) };
  });

  // Save subscription to localStorage
  useEffect(() => {
    localStorage.setItem('plexterra_subscription', JSON.stringify(subscription));
  }, [subscription]);

  // Reset monthly counter if new month
  useEffect(() => {
    const currentMonth = new Date().toISOString().slice(0, 7);
    if (subscription.lastResetDate !== currentMonth) {
      setSubscription(prev => ({
        ...prev,
        analysesUsed: 0,
        lastResetDate: currentMonth
      }));
    }
  }, [subscription.lastResetDate]);

  // Get current tier config
  const currentTier = SUBSCRIPTION_TIERS[subscription.tier] || SUBSCRIPTION_TIERS.FREE;

  // Check if user can perform analysis
  const canAnalyze = () => {
    if (currentTier.analysesPerMonth === Infinity) return true;
    return subscription.analysesUsed < currentTier.analysesPerMonth;
  };

  // Get remaining analyses
  const remainingAnalyses = () => {
    if (currentTier.analysesPerMonth === Infinity) return Infinity;
    return Math.max(0, currentTier.analysesPerMonth - subscription.analysesUsed);
  };

  // Increment analysis count
  const incrementAnalysisCount = () => {
    setSubscription(prev => ({
      ...prev,
      analysesUsed: prev.analysesUsed + 1
    }));
  };

  // Check if feature is available
  const hasFeature = (featureName) => {
    return currentTier.features[featureName] || false;
  };

  // Upgrade subscription
  const upgradeTo = (tierName) => {
    if (SUBSCRIPTION_TIERS[tierName]) {
      setSubscription(prev => ({
        ...prev,
        tier: tierName
      }));
    }
  };

  // Check if current tier is the given tier or higher
  const isTierOrHigher = (tierName) => {
    const tierOrder = ['FREE', 'PRO', 'DEVELOPER'];
    const currentIndex = tierOrder.indexOf(subscription.tier);
    const checkIndex = tierOrder.indexOf(tierName);
    return currentIndex >= checkIndex;
  };

  const value = {
    subscription,
    currentTier,
    canAnalyze,
    remainingAnalyses,
    incrementAnalysisCount,
    hasFeature,
    upgradeTo,
    isTierOrHigher,
    SUBSCRIPTION_TIERS
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};
